import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import {
    FormBuilder,
    Validators,
    ReactiveFormsModule,
    FormGroup
} from '@angular/forms';
import { PlantsService } from '../../services/plants.service';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
export type NewCityDto = {
    title: string;
    timeZoneId: string;
    latitude: number;
    longitude: number;
};
export type CityOption = {
    id: number;
    title: string;
    timeZoneId: string;
    latitude: number;
    longitude: number;
};
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MapDialogComponent, MapResult } from '../map-dialog/map-dialog.component';

@Component({
    selector: 'app-add-new-city-modal',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatAutocompleteModule,
        MatProgressSpinnerModule,
        MatOptionModule,
        MatSelectModule,
        MatIconModule,
        TranslatePipe
    ],
    templateUrl: './add-new-city-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './add-new-city-modal.component.scss'
})
export class AddNewCityModalComponent {
    form: FormGroup = this.fb.group({
        title: ['', [Validators.required, Validators.pattern(/\S/)]],
        timeZoneId: ['', Validators.required],
        latitude: [null, [Validators.required, Validators.min(-90), Validators.max(90)]],
        longitude: [null, [Validators.required, Validators.min(-180), Validators.max(180)]]
    });

    loading = false;
    timeZones: string[] = [];
    selectedAddress: string | null = null;

    constructor(
        private fb: FormBuilder,
        private plantsSvc: PlantsService,
        private dialog: MatDialog,
        private ref: MatDialogRef<AddNewCityModalComponent, CityOption>,
        private sb: MatSnackBar,
        private translate: TranslateService,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {}

    ngOnInit() {
        this.plantsSvc.getTimeZones().subscribe({
            next: res => {
                this.timeZones = (res?.timeZones ?? [])
                    .map((z: any) => z.title)
                    .sort();
            },
            error: () => {
                this.timeZones = [];
            }
        });
    }

    close() {
        this.ref.close();
    }

    openMap(): void {
        if (this.loading) return;

        const latitude = this.form.controls['latitude'].value;
        const longitude = this.form.controls['longitude'].value;
        const initial = this.hasValidCoordinates(latitude, longitude)
            ? {
                lat: Number(latitude),
                lng: Number(longitude),
                displayName: this.selectedAddress ?? undefined
            }
            : undefined;

        const mapRef = this.dialog.open(MapDialogComponent, {
            width: '920px',
            maxWidth: '96vw',
            maxHeight: '94vh',
            autoFocus: false,
            restoreFocus: true,
            data: { initial }
        });

        mapRef.afterClosed().subscribe((result: MapResult | undefined) => {
            if (!result) return;

            this.form.patchValue({
                latitude: this.roundCoordinate(result.lat),
                longitude: this.roundCoordinate(result.lng)
            });

            this.selectedAddress = result.displayName;
            this.form.controls['latitude'].markAsDirty();
            this.form.controls['longitude'].markAsDirty();
            this.form.controls['latitude'].updateValueAndValidity();
            this.form.controls['longitude'].updateValueAndValidity();
            this.form.updateValueAndValidity();
        });
    }

    clearSelectedAddress(): void {
        this.selectedAddress = null;
    }

    private hasValidCoordinates(latitude: unknown, longitude: unknown): boolean {
        const lat = Number(latitude);
        const lng = Number(longitude);

        return Number.isFinite(lat) && Number.isFinite(lng)
            && lat >= -90 && lat <= 90
            && lng >= -180 && lng <= 180;
    }

    private roundCoordinate(value: number): number {
        return Number(value.toFixed(7));
    }

    submit() {
        if (this.loading) return;

        this.form.updateValueAndValidity();

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const dto = this.form.getRawValue() as NewCityDto;
        this.loading = true;
        this.plantsSvc
            .addNewCity(
                dto.title.trim(),
                dto.timeZoneId.trim(),
                Number(dto.latitude),
                Number(dto.longitude)
            )
            .subscribe({
            next: city => {
                this.loading = false;
                this.notify(this.translate.instant('City added successfully'), 'success');
                this.ref.close(city);
            },
            error: () => {
                this.notify(this.translate.instant('City registration failed.'), 'error');
                this.loading = false;
            }
        });
    }

    private notify(
        msg: string,
        type: 'success' | 'error' | 'warn' | 'info' = 'info',
        duration = 3500
    ) {
        const cls =
            type === 'success'
                ? 'snack-success'
                : type === 'error'
                ? 'snack-error'
                : type === 'warn'
                ? 'snack-warn'
                : 'snack-info';

        this.sb.open(msg, this.translate.instant('close'), {
            duration,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: [cls]
        });
    }
}
